/**
 * Editorial-dash template that creates a Question instance asking respondents
 * from a curated set of perspectives to weigh in.
 *
 * A creation-time preset registered in the `templates` config slot, but unlike
 * the simpler templates this one carries both a `configWidget` (shown during
 * new-space creation, with inline guidance on writing good perspectives) and an
 * `editWidget` (shown later on the question's edit screen via
 * `SaveableTextField`). The `perspectives` string is captured into `spaceProps`
 * at creation and written by `afterCreateAsync` once the space exists;
 * `isCreateAllowed` blocks the create button until perspectives are entered.
 * Both widgets go through the `spaces.perspectives` endpoint. The runtime
 * perspective-selection UX lives in the `representing_perspectives` feature
 * this template enables.
 */

import {
    containsLetterOrEmoji,
    formatPerspectiveLines,
    parsePerspectiveLines,
    perspectiveVoteLabel,
    sanitizePerspectiveLabel,
} from '@repo/shared/perspectives';
import { useTheme } from '@repo/theme';
import { VertBox } from 'component/basics';
import { FormField } from 'component/form';
import { SaveableTextField } from 'component/form/saveable-text-field';
import { BulletText } from 'component/richtext';
import { TextArea, UtilityText } from 'component/text';
import { setSpacePerspectivesAsync, useSpacePerspectives } from 'data/perspectives';
import type { SpaceTemplate } from 'feature/editorial-dash/spaces';
import { ConversationFormatHelp } from 'feature/editorial-dash/spaces/create';
import { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { useDatastore } from 'util/datastore';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

type ConfigWidgetProps = {
    spaceProps: any;
    setSpaceProps: (props: any) => void;
};

function ConfigWidget({ spaceProps, setSpaceProps }: ConfigWidgetProps) {
    const { colors } = useTheme();
    const [isFocused, setIsFocused] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsFocused(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref]);

    const descriptionComponent = (
        <VertBox gap={4}>
            <BulletText
                color={colors.colorTextSecondary}
                label={t(
                    'representing_perspectives_template.description_1',
                    'Choose **3-5 specific perspectives** you want to hear from (e.g. "🏖️ Retiree," "📦 New resident," "🍼 Working parent," etc.)',
                )}
            />
            <BulletText
                color={colors.colorTextSecondary}
                label={t(
                    'representing_perspectives_template.description_2',
                    '**Avoid demographic-based perspectives** (e.g. "urban," "low income," "woman," etc.) that might lead to stereotypes',
                )}
            />
            <BulletText
                color={colors.colorTextSecondary}
                label={t(
                    'representing_perspectives_template.description_3',
                    '**Avoid prescribing beliefs to perspectives** (e.g. "person with conservative attitude," or "supporters" and "opponents")',
                )}
            />
            <BulletText
                color={colors.colorTextSecondary}
                label={t('representing_perspectives_template.description_4', '**Choose an emoji** for each perspective')}
            />
        </VertBox>
    );

    const template = {
        label: t('representing_perspectives_template.helper_label', 'Define Your Perspectives'),
        descriptionComponent,
    } as SpaceTemplate;

    const validationError = spaceProps.perspectives ? perspectiveLinesValidationLabel(spaceProps.perspectives) : undefined;

    return (
        <View ref={ref as any}>
            <FormField
                descriptionLabel={t('representing_perspectives_template.description_5', 'Separate perspectives with a line break.')}
                errorLabel={validationError}
                label={t('representing_perspectives_template.form_field_label', 'Perspectives')}
                required
            >
                <TextArea
                    onChange={(text) => setSpaceProps({ ...spaceProps, perspectives: text })}
                    onFocusChange={(focused) => setIsFocused(focused || isFocused)}
                    placeholderLabel={t('representing_perspectives_template.placeholder', 'Write 3-5 perspectives...')}
                    value={spaceProps.perspectives}
                />
                {isFocused && <ConversationFormatHelp showPlayBookLink={true} template={template} topOffset={27} />}
            </FormField>
        </View>
    );
}

/**
 * The error label for an invalid perspective line, or
 * undefined when all are valid. Rejects labels consisting
 * only of digits, punctuation, or symbols (no letters or emoji).
 */
function perspectiveLinesValidationLabel(text: string): TLabel | undefined {
    const perspectives = parsePerspectiveLines(text);
    for (const p of perspectives) {
        const fullLabel = sanitizePerspectiveLabel(perspectiveVoteLabel(p));
        if (fullLabel && !containsLetterOrEmoji(fullLabel)) {
            return t('perspectives.edit_invalid_characters', 'Perspective labels must contain at least one letter');
        }
    }
    return undefined;
}

// The textarea edits one perspective per line; the endpoint speaks entries. So
// the field is formatted from the list on the way in and parsed back on save.
function EditWidget() {
    const { colors } = useTheme();
    const datastore = useDatastore();
    const { perspectives } = useSpacePerspectives();
    const [validationLabel, setValidationLabel] = useState<TLabel | undefined>(undefined);
    return (
        <View>
            <SaveableTextField
                descriptionLabel={t('template.perspectives_description', 'Separate perspectives with a line break.')}
                error={validationLabel ? <UtilityText color={colors.colorTextSecondary} label={validationLabel} type='small' /> : undefined}
                label={t('template.perspectives_label', 'Perspectives')}
                multiline
                onChange={() => setValidationLabel(undefined)}
                onSave={async (text) => {
                    const label = perspectiveLinesValidationLabel(text);
                    if (label) {
                        setValidationLabel(label);
                        throw new Error('Invalid perspective labels');
                    }
                    await setSpacePerspectivesAsync(datastore, {
                        spaceKey: datastore.getInstanceKey(),
                        perspectives: parsePerspectiveLines(text),
                    });
                }}
                required
                testID='perspectives-input'
                value={formatPerspectiveLines(perspectives)}
            />
        </View>
    );
}

export const RepresentingPerspectivesTemplate: SpaceTemplate = {
    key: 'representing-perspectives',
    label: t('representing_perspectives_template.label', 'Representing Perspectives'),
    descriptionLabel: t(
        'representing_perspectives_template.description',
        'Ask users from a specific set of perspectives to respond to your question.',
    ),
    imageUrl: 'images/representing-perspectives.svg',
    structureKey: 'question',
    featuresEnabled: ['representing_perspectives'],
    configWidget: ConfigWidget,
    editWidget: EditWidget,
    afterCreateAsync: async ({ datastore, spaceKey, spaceProps }) =>
        await setSpacePerspectivesAsync(datastore, { spaceKey, perspectives: parsePerspectiveLines(spaceProps.perspectives) }),
    isCreateAllowed: ({ spaceProps }) => !!spaceProps.perspectives && !perspectiveLinesValidationLabel(spaceProps.perspectives),
};
